import * as React from "react";
import { observer } from "mobx-react-lite";
import { StringModel, StringMobx } from "../../mobxDataModels/stringModel";
import { NameModel } from "../../mobxDataModels/nameModel";
import { notification, Modal } from "antd";
import { nameOfKind, parentLink, request, resourceLink } from "../../services/cpln";
import { clearItem, randomName } from "../../services/utils";
import { useNavigate } from "react-router-dom";
import { NGButton } from "../../newcomponents/button/Button";
import { NGFormElement } from "../../newcomponents/ngformelement/ngformelement";

interface Props {
  kind: string;
  link: string;
  name: string;
  onClose: () => void;
  clone: (item: any) => Promise<void>;
  nameGenerator?: () => string;
}

const CloneModalRaw: React.FC<Props> = ({ kind, link, name, clone, onClose, nameGenerator = randomName }) => {
  const nameRef = React.useRef<StringMobx>(
    NameModel.create({
      value: kind === "policy" ? `${name}-copy--${nameGenerator()}` : "",
      validationKey: "name",
    }),
  );
  const descriptionRef = React.useRef<StringMobx>(StringModel.create({ label: "Description" }));

  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();

  async function tryConfirmClone() {
    try {
      if (!nameRef.current.isValid) return;
      setIsLoading(true);

      let _itemToClone: any;
      if (kind === "secret") {
        const { data: dataSecret } = await request({ url: link + "/-reveal" });
        _itemToClone = dataSecret;
      } else {
        const { data } = await request({ url: link });
        _itemToClone = data;
      }

      // override description
      if (descriptionRef.current.value) {
        _itemToClone.description = descriptionRef.current.value;
      } else {
        _itemToClone.description = nameRef.current.value;
      }

      // override name
      _itemToClone.name = nameRef.current.value;

      clearItem(_itemToClone);

      await clone(_itemToClone);
      notification.success({
        message: "Success",
        description: `Cloned ${nameOfKind(kind)}`,
      });

      onClose();
      setIsLoading(false);

      navigate(`/console${parentLink(kind as any)}`);
      setTimeout(() => {
        navigate(`/console${resourceLink(kind as any, _itemToClone.name)}`);
      }, 200);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  return (
    <Modal
      open={true}
      closable={false}
      onCancel={onClose}
      footer={
        <div className="modal-actions">
          <NGButton variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </NGButton>
          <NGButton
            variant="primary"
            data-testid="clone-modal"
            onClick={tryConfirmClone}
            loading={isLoading}
            disabled={isLoading || !nameRef.current.isValid}
          >
            Clone
          </NGButton>
        </div>
      }
      maskClosable={!isLoading}
      destroyOnClose
    >
      <div className="flex flex-col" style={{ padding: "16px 8px 0" }}>
        <NGFormElement
          required
          name="name"
          label={nameRef.current.label}
          value={nameRef.current.value}
          onChange={nameRef.current.setValue}
        />
        <NGFormElement
          name="description"
          label={descriptionRef.current.label}
          value={descriptionRef.current.value}
          onChange={descriptionRef.current.setValue}
        />
      </div>
    </Modal>
  );
};

export const CloneModal = observer(CloneModalRaw);
